import React, { useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import Cropper from "react-easy-crop";
import Modal from "@mui/material/Modal";

const UploadFileToDatabase = ({ files, setFiles, progress }) => {
	const inputFile = useRef(null);
	const [previewURLs, setPreviewURLs] = useState([]);

	// CROP STATES
	const [cropModalOpen, setCropModalOpen] = useState(false);
	const [cropImageSrc, setCropImageSrc] = useState(null);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
	const [cropIndex, setCropIndex] = useState(null);
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);

	// HELPERS
	const createImage = (url) =>
		new Promise((resolve, reject) => {
			const image = new Image();
			image.onload = () => resolve(image);
			image.onerror = (err) => reject(err);
			image.src = url;
		});

	const getCroppedImg = async (imageSrc, croppedAreaPixels) => {
		const image = await createImage(imageSrc);

		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");

		canvas.width = croppedAreaPixels.width;
		canvas.height = croppedAreaPixels.height;

		ctx.drawImage(
			image,
			croppedAreaPixels.x,
			croppedAreaPixels.y,
			croppedAreaPixels.width,
			croppedAreaPixels.height,
			0,
			0,
			croppedAreaPixels.width,
			canvas.height,
		);

		return new Promise((resolve) => {
			canvas.toBlob((blob) => resolve(blob), "image/jpeg", 1);
		});
	};

	// Clean up URLs on unmount
	useEffect(() => {
		return () => {
			previewURLs.forEach((preview) => URL.revokeObjectURL(preview.url));
		};
	}, [previewURLs]);

	// // Clear previews when files are empty
	// useEffect(() => {
	// 	if (!files || files.length === 0) {
	// 		previewURLs.forEach((preview) => URL.revokeObjectURL(preview.url));
	// 		setPreviewURLs([]);
	// 	}
	// }, [files]);

	const handleChange = (e) => {
		const selectedFiles = Array.from(e.target.files);

		const allowedTypes = [
			"video/mp4",
			"video/quicktime", // MOV
			"video/x-msvideo", // AVI
			"image/jpeg",
			"image/jpg",
			"image/png",
			"image/webp",
			"image/gif",
		];

		const maxFileSize = 50 * 1024 * 1024; // 50MB
		const totalFiles = [...files, ...selectedFiles];

		if (totalFiles.length > 4) {
			toast.warn("You can upload a maximum of 4 images.");
			return;
		}

		const isDuplicate = (f) =>
			files.some(
				(existing) =>
					existing.name === f.name &&
					existing.size === f.size &&
					existing.type === f.type,
			);

		const validFiles = selectedFiles.filter((f) => {
			if (!allowedTypes.includes(f.type)) {
				toast.warn(`${f.name} is not a supported file format.`);
				return false;
			}

			if (f.size > maxFileSize) {
				toast.warn(`${f.name} exceeds the 50MB size limit.`);
				return false;
			}

			if (isDuplicate(f)) {
				toast.warn(`${f.name} has already been selected.`);
				return false;
			}

			return true;
		});

		// Generate previews for new image files
		const imageFiles = validFiles.filter((f) =>
			f.type.startsWith("image/"),
		);

		const formattedFiles = validFiles.map((f) => ({
			file: f,
			croppedBlob: null,
			type: f.type.startsWith("image") ? "image" : "video",
			preview: URL.createObjectURL(f),
		}));

		// const imagePreviews = imageFiles.map((f) => ({
		// 	name: f.name,
		// 	url: URL.createObjectURL(f),
		// }));

		setFiles((prev) => [...prev, ...formattedFiles]);

		setPreviewURLs((prev) => [
			...prev,
			...formattedFiles
				.filter((f) => f.type === "image")
				.map((f) => ({
					name: f.file.name,
					url: f.preview,
				})),
		]);
	};

	// OPEN CROP MODAL
	const openCrop = (index) => {
		setCropIndex(index);
		setCropImageSrc(files[index].preview);
		setCropModalOpen(true);
	};

	// APPLY CROP
	const applyCrop = async () => {
		const blob = await getCroppedImg(cropImageSrc, croppedAreaPixels);
		const croppedPreviewUrl = URL.createObjectURL(blob);
		setFiles((prev) => {
			const updated = [...prev];
			updated[cropIndex] = {
				...updated[cropIndex],
				croppedBlob: blob,
				preview: croppedPreviewUrl, // only override this image's preview
			};
			return updated;
		});
		setCropModalOpen(false);
	};

	useEffect(() => {
		return () => {
			files.forEach((f) => {
				if (f?.preview) URL.revokeObjectURL(f.preview);
			});
		};
	}, []);

	return (
		<div>
			<ToastContainer />
			<button
				type="button"
				onClick={() => inputFile.current.click()}
				className="file-choose-btn"
			>
				Choose images
			</button>
			<input
				type="file"
				accept=".mp4,.mov,.avi,.jpg,.jpeg,.png,.webp,.gif"
				multiple
				onChange={handleChange}
				ref={inputFile}
				style={{ display: "none" }}
			/>

			{/* IMAGE PREVIEWS + CROP BUTTON */}
			<div className="pt-3 d-flex justify-content-center">
				{files.length > 0 ? (
					<div style={{ display: "flex", gap: 10 }}>
						{files.map((f, idx) =>
							f.type === "image" ? (
								<div key={idx} style={{ textAlign: "center" }}>
									<div className="selected-iamge-preview">
										<img
											src={f.preview}
											style={{
												width: "100%",
												height: "100%",
												objectFit: "cover", // Instagram behavior
											}}
											onClick={() => openCrop(idx)}
										/>
									</div>
									<div className="progress-bar mt-2">
										<div
											className="progress-fill"
											style={{ width: `${progress}%` }}
										>
											{progress}%
										</div>
									</div>
									<button
										className="file-choose-btn mt-3"
										style={{ fontSize: 10 }}
										onClick={() => openCrop(idx)}
									>
										<span>Crop</span>
									</button>
								</div>
							) : (
								<div key={idx}>
									<video
										src={f.preview}
										width="60"
										height="60"
										style={{ borderRadius: 6 }}
									/>
								</div>
							),
						)}
					</div>
				) : (
					<span className="text-secondary">selected images</span>
				)}
			</div>

			{/* CROP MODAL */}
			<Modal open={cropModalOpen}>
				<div className="cropper-upload-modal">
					<div className="cropper-upload-box">
						<div className="cropper-upload-area">
							<Cropper
								image={cropImageSrc}
								crop={crop}
								zoom={zoom}
								aspect={3 / 5}
								onCropChange={setCrop}
								onZoomChange={setZoom}
								onCropComplete={(
									croppedArea,
									croppedAreaPixels,
								) => {
									setCroppedAreaPixels(croppedAreaPixels);
								}}
							/>
						</div>
						<div className="crop-upload-buttons">
							<button
								className="btn btn-primary"
								onClick={applyCrop}
							>
								Apply Crop
							</button>

							<button
								className="btn btn-secondary"
								onClick={() => setCropModalOpen(false)}
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default UploadFileToDatabase;
